<template>
    <div class="electrictiy-bill-calculator__result-chart-container">
        <canvas id="resultMonthChart"></canvas>
        <div class="legend-container" id="month-chart-legend-container"></div>
    </div>
</template>

<script setup>
    import util from '../util';
    import { useWizardStore } from '../stores/WizardStore';
    import { storeToRefs } from "pinia";
    
    const store = useWizardStore()
    const { appSettings, calculatedPrice } = storeToRefs(store);
   
    const props = defineProps({
        colors: { type: Object, required: true },
    });

    let monthChart;
    const yAxisLabel = 'KR/MÅN';

    const destroyChart = () => {
        if(monthChart) {
            monthChart.destroy();
        }
    }

    const getDataset = () => {
        let monthChartDataset =  [       
            {
                label: appSettings.value.FixedChargeText,
                data: getMonthlyCost('FixedCharge'),
                backgroundColor: props.colors.FixedCharge.backgroundColor,
                hoverBackgroundColor: props.colors.FixedCharge.backgroundColor,
                order: 1,
                maxBarThickness: 30,
                hoverBorderWidth: 3,
                borderWidth:0,
                borderColor:'#000',
                borderSkipped:'middle',
            },             
            {
                label: appSettings.value.TransferCostText,
                data: getMonthlyCost('TransferCost'),
                backgroundColor: props.colors.TransferCost.backgroundColor,
                hoverBackgroundColor: props.colors.TransferCost.backgroundColor,
                order: 3,
                maxBarThickness: 30,
                hoverBorderWidth: 3,
                borderWidth:0,
                borderColor:'#000',            
                borderSkipped:'middle',
            },
            {
                label: appSettings.value.EnergyTaxCostText,
                data: getMonthlyCost('EnergyTaxCost'),
                backgroundColor: props.colors.EnergyTaxCost.backgroundColor,
                hoverBackgroundColor: props.colors.EnergyTaxCost.backgroundColor,
                order: 2,
                maxBarThickness: 30,
                hoverBorderWidth: 3,
                borderWidth:0,
                borderColor:'#000',
                borderSkipped:'middle',
            }                        
        ];  

        if(store.showEffectCostInResult) {
            monthChartDataset.push(getDatasetForEffectCost());
        }

        return monthChartDataset;
    }

    const getDatasetForEffectCost = () => {
        return  {
            label: appSettings.value.EffectCostText,
            data: getMonthlyCost('EffectCost'),
            backgroundColor: props.colors.EffectCost.backgroundColor,
            hoverBackgroundColor: props.colors.EffectCost.backgroundColor,
            order: 4,
            maxBarThickness: 30,
            hoverBorderWidth: 3,
            borderWidth:0,
            borderColor:'#000',
            borderSkipped:'middle',
        };
    }

    const updateChart = () => {        
        if(!monthChart) {
            initMonthChart();
            return;
        }
        monthChart.data.datasets.forEach((dataset) => {
            switch(dataset.label){
                case appSettings.value.TransferCostText:
                    dataset.data = getMonthlyCost('TransferCost');
                    break;
                case appSettings.value.EnergyTaxCostText:
                    dataset.data = getMonthlyCost('EnergyTaxCost');
                    break;  
                case appSettings.value.FixedChargeText:
                    dataset.data = getMonthlyCost('FixedCharge');
                    break;  
                case appSettings.value.EffectCostText:
                    dataset.data = getMonthlyCost('EffectCost');
                    break;
            }
        });
        let effectCostIndex = null;
        monthChart.data.datasets.forEach((dataset, index) => {
            switch(dataset.label){
                case appSettings.value.TransferCostText:
                    dataset.data = getMonthlyCost('TransferCost');
                    break;
                case appSettings.value.EnergyTaxCostText:
                    dataset.data = getMonthlyCost('EnergyTaxCost');
                    break;  
                case appSettings.value.FixedChargeText:
                    dataset.data = getMonthlyCost('FixedCharge');
                    break;  
                case appSettings.value.EffectCostText:
                    effectCostIndex = index;
                    dataset.data = getMonthlyCost('EffectCost');
                    break;
            }
        });

        //Add effect cost if it should be there but isn't. (The case when you have switched from apartment to house/company).
        if(store.showEffectCostInResult && effectCostIndex === null) {
            monthChart.data.datasets.push(getDatasetForEffectCost());            
        }
        //Remove effect cost? (The case when you have switched from house/company to apartment).
        if(!store.showEffectCostInResult && effectCostIndex !== null) {
            monthChart.data.datasets.splice(effectCostIndex, 1);
        }
        monthChart.update();
    }
    
    //Tooltip handler
    const getOrCreateMonthChartTooltip = (chart) => {
        let tooltipEl = document.getElementById('chartjs-month-tooltip')

        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-month-tooltip';
            const table = document.createElement('table');            
            tooltipEl.appendChild(table);
            chart.canvas.parentNode.appendChild(tooltipEl);
        }

        return tooltipEl;
    };

    function getLabelColorMatchingBackgroundColor(bgColor){
        for (const [key, value] of Object.entries(props.colors)) {
            if(value.backgroundColor === bgColor) {
                return value.textColor;
            }
        }        
        return '#000';
    }    

    const monthChartTooltipHandler = (context) => {
        const {chart, tooltip} = context;
        const tooltipEl = getOrCreateMonthChartTooltip(chart);

        // Hide if no tooltip
        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
        }

        // Set Content
        if (tooltip.body) {
            const titleLines = tooltip.title || [];
            const bodyLines = tooltip.body.map(b => b.lines).reverse();
            const labelColors = tooltip.labelColors.reverse();
            const tableHead = document.createElement('thead');

            //Title
            titleLines.forEach(title => {
                const tr = document.createElement('tr');                
                const th = document.createElement('th');
                th.colSpan = "2";
                let titleArray = title.split(' ');
                titleArray[0] = util.getFullMonthNameFromShortMonthName(titleArray[0]);                
                const text = document.createTextNode(titleArray[0]); // show only month
                // const text = document.createTextNode(titleArray.join(' ')); // ex Nov 2023
                th.appendChild(text);
                tr.appendChild(th);
                tableHead.appendChild(tr);
            });

            const tableBody = document.createElement('tbody');
            let totalPrice = 0;
            bodyLines.forEach((body, i) => {                                             
                const colors = labelColors[i];
                const label = body[0].split(':')[0];                
                const price = Number(body[0].split(':')[1].replaceAll(/\s/g,'').replace(',','.').trim());
                const priceText = util.getFormatedPrice(price, 2);
                totalPrice += price;

                //Label
                const span = document.createElement('span');
                span.style.background = colors.backgroundColor;               
                span.style.color = getLabelColorMatchingBackgroundColor(colors.backgroundColor);               
                span.innerHTML = label;               
                const tdLabel = document.createElement('td');
                tdLabel.appendChild(span);

                //Price
                const tdPrice = document.createElement('td');
                const text = document.createTextNode(priceText);
                tdPrice.appendChild(text);
                
                //Table row
                const tr = document.createElement('tr');
                tr.appendChild(tdLabel);
                tr.appendChild(tdPrice);
                tableBody.appendChild(tr);
            });

            //Total label
            const tr = document.createElement('tr');
            const tdLabel = document.createElement('td');
            tdLabel.innerHTML = 'Totalt';
            
            //Total price
            const tdPrice = document.createElement('td');
            const text = document.createTextNode(util.getFormatedPrice(totalPrice, 2));
            tdPrice.appendChild(text);
            
            tr.appendChild(tdLabel);
            tr.appendChild(tdPrice);
            tableBody.appendChild(tr);


            const tableRoot = tooltipEl.querySelector('table');

            // Remove old children
            while (tableRoot.firstChild) {
                tableRoot.firstChild.remove();
            }

            // Add new children
            tableRoot.appendChild(tableHead);
            tableRoot.appendChild(tableBody);
        }
        
        const {
            offsetLeft: positionX,
            offsetTop: positionY
        } = chart.canvas;

        // Display, position, and set styles for font
        //Make sure tooltip is not outside canvas to the left
        let leftPos = Math.max(positionX + tooltip.caretX, tooltipEl.offsetWidth/2);
        //Make sure tooltip is not outside canvas to the right
        leftPos = Math.min(leftPos, chart.canvas.offsetWidth - tooltipEl.offsetWidth/2);        
        tooltipEl.style.opacity = 1;
        tooltipEl.style.left = leftPos + 'px';
       // tooltipEl.style.top = positionY + tooltip.caretY + 'px';
        tooltipEl.style.top = 0 + 'px';
        tooltipEl.style.translate = '0 50%';
        tooltipEl.style.font = tooltip.options.bodyFont.string;
        tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
    };        

    const getMonthlyCost = (costName) => {
        let monthlyData = [];        
        if(calculatedPrice.value.monthly === null) {
            return null;
        }                
        Object.keys(calculatedPrice.value.monthly).forEach(key => {
            monthlyData.push(calculatedPrice.value.monthly[key][costName]);
        });
        return monthlyData;
    }
    
    //Draw bar chart with monhtly data
    const initMonthChart = () =>{          
        destroyChart();
             
        var monthChartCanvas = "resultMonthChart";             
        const ctx = document.getElementById(monthChartCanvas).getContext('2d');        
        monthChart = new Chart(ctx, {
            type: 'bar',
            plugins:[{
                afterDraw: chart => {
                    //var ctx = chart.chart.ctx;
                    ctx.save();
                    ctx.font = "16px Viga";
                    ctx.fillStyle = "#000";                    
                    ctx.textAlign = 'left';        
                    ctx.fillText(yAxisLabel, 50, 20);
                    
                    ctx.restore();
                }
            }],
            data: {
                labels: util.getArrayOfMonthNames(), //util.getArrayOfMonthNamesWithCurrentYear(),
                datasets: getDataset()
            },
            options: {
                legend: {
                     display: false
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 40,
                        bottom: 0
                    }
                },
                plugins: {                       
                    legend: {
                        display: false,
                    },             
                    deferred: {
                        xOffset: 150,
                        yOffset: '50%',
                        delay: 1000
                    },
                    tooltip: {
                        enabled: false,
                        external: monthChartTooltipHandler
                    }                                    
                },
                maintainAspectRatio: false,
                interaction: {
                    mode: 'x'
                },                
                scales: {
                    yAxes: {
                        grid: {
                            display: false,
                            borderColor: '#707070',
                        },
                        stacked: true,
                        ticks: {
                            beginAtZero: true,
                            color: '#000',
                            font: {
                                size: 14,
                                weight: 'normal',
                                family: 'Viga'
                            },
                        }
                    },
                    xAxes: {
                        grid: {
                            display: false,
                            borderColor: '#707070',
                            z: 1
                        },                        
                        stacked: true,
                    
                        ticks: {
                            color: '#000',
                            font: {
                                size: 14,
                                family: 'Calibri'
                            },
                        }
                    }
                },             
            }
	    });            
    }

    defineExpose({
        initMonthChart,
        updateChart
    })
</script>

<style scoped>
</style>
